import { Button, Flex, HStack, useToast } from '@chakra-ui/react';
import { type DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { CandidateListTypeDropdown } from '@/components';
import { useExportCandidatesMutation } from '@/features/admin-candidates';
import { useShareToken } from '@/features/admin-interview';
import { type CandidateListType, type InterviewStatus } from '@/services/api';

import {
  getRangeScoreFrom,
  getRangeScoreTo,
} from '../../components/Filters/Range/RangeFilterComponent';
import {
  type RatingObj,
  type ratingItems,
} from '../../components/Filters/Rating/RatingFilterComponent';
import { IconExcel } from '../../components/Icons';
import { AddCandidate } from './AddCandidate';
import { type StatusItemsObj } from './CandidatesFilters';

interface HeaderSectionProps {
  totalCandidates: number | string;
  interviewId?: string;
  rangeSearchTerm: Array<number>;
  status: StatusItemsObj;
  rating: RatingObj;
  dateRangeSearchTerm: DateRange;
  searchTerm: string;
  candidateListType: CandidateListType;
  setCandidateListType: (value: CandidateListType) => void;
}

export const CandidateListHeaderSection: React.FC<HeaderSectionProps> = ({
  totalCandidates,
  interviewId,
  dateRangeSearchTerm,
  rangeSearchTerm,
  rating,
  status,
  searchTerm,
  candidateListType,
  setCandidateListType,
}) => {
  const { t } = useTranslation(['common', 'candidate']);
  const toast = useToast();
  const shareToken = useShareToken();

  // TODO refactor, filter of useGetInterviewCandidatesQuery should match this
  const exportExcelCandidatesMutation = useExportCandidatesMutation({
    axiosRequestConfig: {
      overrideSecurityToken: shareToken,
    },
  });

  const handleExportCandidates = async () => {
    try {
      const s3Url = await exportExcelCandidatesMutation.mutateAsync({
        interviewsId: interviewId,
        q: searchTerm,
        dateFrom: dateRangeSearchTerm.from,
        dateTo: dateRangeSearchTerm.to,
        status: Object.keys(status)
          .filter((k) => status[k as InterviewStatus])
          .map((k) => k as InterviewStatus),
        ratings: Object.keys(rating)
          .filter((k) => rating[k as (typeof ratingItems)[number]])
          .map((k) => Number(k)),
        aiScoreFrom: getRangeScoreFrom(rangeSearchTerm),
        aiScoreTo: getRangeScoreTo(rangeSearchTerm),
      });

      // Open the S3 URL in a new tab
      window.open(s3Url, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: t('common:errors.downloadFileFailed'),
        status: 'error',
      });
    }
  };

  return (
    <>
      <Flex align="center" py="20px" px="24px" justify="space-between">
        <Flex gap={3} align="center">
          <CandidateListTypeDropdown
            selectedListType={candidateListType}
            onListTypeChange={setCandidateListType}
          >
            {`${t('candidate:totalCandidate')} (${totalCandidates})`}
          </CandidateListTypeDropdown>
        </Flex>

        <HStack rowGap={3}>
          <Button
            onClick={handleExportCandidates}
            size="md"
            isLoading={exportExcelCandidatesMutation.isPending}
            leftIcon={<IconExcel fontSize={20} />}
            isDisabled={totalCandidates === 0}
          >
            {t('common:actions.exportToExcel')}
          </Button>

          {interviewId ? <AddCandidate interviewId={interviewId} /> : null}
        </HStack>
      </Flex>
    </>
  );
};
